import { Loader, Swiper } from '@services';
import { ATTRIBUTES, BaseBundle } from './base-bundle.js';
import '@modules';
import './debounce-Dg7s1ivP.js';

/**
 * Extends the shared ATTRIBUTES map with BundleBuilder-specific data attributes
 * for Swiper navigation, step indicators, and sub-slide parent/option linking.
 */
Object.assign(ATTRIBUTES, {
  SWIPER: {
    EL: 'data-bundle-swiper',
    NEXT: 'data-next-button',
    PREV: 'data-previous-button',
    SLIDE: 'data-slide',
  },
  STEPS: {
    LIST: 'data-steps',
    BUTTON: 'data-bundle-step',
    ACTIVE: 'data-active',
  },
  PARENT_ID: 'data-parent-id',
  PARENT_OPTION: 'data-parent-option',
  HAS_SUBOPTIONS: 'data-has-suboptions',
});

/**
 * Custom element for the step-by-step Bundle Builder.
 * Extends BaseBundle with a Swiper-driven slide flow, per-slide min/max enforcement,
 * radio/checkbox selection tracking, and conditional sub-slide injection.
 */
class BundleBuilder extends BaseBundle {
  /** @type {import('swiper').Swiper | null} Swiper instance managing the step slides. */
  #swiper = null;

  /** @type {Map<string, HTMLElement[]>} parentSlideId → cloned sub-slide elements, keyed by parent slide id. */
  #subSlides = new Map();

  /** @type {HTMLFormElement | null} The form element wrapping all product selection inputs. */
  #form = this.querySelector('form');

  /** @type {Array<string>} Currently selected radio/checkbox input values (variant ids or option values). */
  #selections = this.#getSelections();

  /** @type {InstanceType<typeof Loader> | null} */
  #loader = null;

  /** @returns {string} Storage key scoped to the bundle-builder prefix. */
  get storageKey() {
    return `bundle-builder:${this.dataset.storageKey}`;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle + Setup
  // ---------------------------------------------------------------------------

  /** Caches sub-slides, initialises Swiper, binds listeners, restores state, and runs the first UI update. */
  async connectedCallback() {
    this.#loader = new Loader(this, { fixed: true });
    this.#loader.show();

    this.#subSlides = this.#cacheSubSlides();
    await this.#initSwiper();
    this.#bindBuilderListeners();

    super.connectedCallback();

    this.#syncSubSlidesFromCheckedInputs();
    this.#updateSelections();
    this.updateUI();
    this.#redirectToFirstIncompleteSlide();

    this.#loader.hide();
  }

  /** Delegates form input events to #onInputChange and step button clicks to #onStepButtonClick. */
  #bindBuilderListeners() {
    this.#form?.addEventListener('input', (event) => {
      if (event.target.matches(`[${ATTRIBUTES.PRODUCT_CARD.SELECTION_INPUT}]`)) {
        this.#onInputChange(event.target);
      }
    });

    const steps = [...this.select(this, ATTRIBUTES.STEPS.BUTTON, true)];
    steps.forEach((step) => {
      step.addEventListener('click', this.#onStepButtonClick.bind(this));
    });

    this.#swiper?.on('slideChange', this.#onSlideChange.bind(this));
  }

  // ---------------------------------------------------------------------------
  // Swiper + Navigation
  // ---------------------------------------------------------------------------

  /** Initialises the Swiper instance with fade effect, hash navigation, and prev/next controls. */
  async #initSwiper() {
    const { EL, NEXT, PREV } = ATTRIBUTES.SWIPER;
    this.#swiper = await Swiper(this.select(this, EL), {
      modules: ['EffectFade', 'Manipulation', 'HashNavigation', 'Navigation'],
      effect: 'fade',
      fadeEffect: { crossFade: true },
      allowTouchMove: false,
      hashNavigation: true,
      keyboard: false,
      mousewheel: false,
      navigation: {
        nextEl: this.select(this, NEXT),
        prevEl: this.select(this, PREV),
      },
    });
  }

  /**
   * Guards against landing on a slide (via hash navigation or back/forward) when an earlier
   * slide is still incomplete. If any slide before the active one fails #isSlideComplete,
   * redirects to the first slide.
   */
  #redirectToFirstIncompleteSlide() {
    if (!this.#swiper) return;

    const { activeIndex, slides } = this.#swiper;
    const firstIncompleteIndex = slides.findIndex((slide) => !this.#isSlideComplete(slide));

    if (firstIncompleteIndex === -1 || firstIncompleteIndex >= activeIndex) return;

    this.#swiper.slideTo(0, 0, false);
  }

  /**
   * Syncs the next button and all step buttons to reflect the current active slide and completion state.
   * Disables the next button when the active slide is incomplete; enables step buttons up to the
   * furthest reachable slide.
   */
  #updateNavigationState() {
    const { activeIndex, slides } = this.#swiper;
    const { SWIPER, STEPS } = ATTRIBUTES;
    const slidesData = new Map(slides?.map((slide) => [slide.id, this.#isSlideComplete(slide)]));

    const activeSlideId = slides?.[activeIndex]?.id;
    const isActiveSlideComplete = slidesData.get(activeSlideId);

    // Next button
    this.select(this, SWIPER.NEXT).disabled = !isActiveSlideComplete;

    // Step buttons
    const stepsLists = this.select(this, STEPS.LIST, true);
    stepsLists.forEach((stepsList) => {
      const steps = [...this.select(stepsList, STEPS.BUTTON, true)];
      const activeStepIndex = steps.findIndex((step) => step.dataset.target === activeSlideId);

      let chainComplete = isActiveSlideComplete;

      steps.forEach((step, index) => {
        const { target } = step.dataset;
        const isVisible = slidesData.has(target);

        // Set active step
        if (index === activeStepIndex) {
          step.setAttribute(STEPS.ACTIVE, 'true');
        } else {
          step.removeAttribute(STEPS.ACTIVE);
        }

        // Show/hide steps for subslides
        step.classList.toggle('hidden', !isVisible);

        // Steps up to and including the active one are always enabled.
        if (index <= activeStepIndex) {
          step.disabled = false;
          return;
        }
        if (!isVisible) {
          step.disabled = true;
          return;
        }
        step.disabled = !chainComplete;
        chainComplete = chainComplete && slidesData.get(target);
      });
    });
  }

  /**
   * Detaches all sub-slides (slides with a `data-parent-id`) from the DOM on init and stores
   * deep clones in #subSlides, keyed by parent slide id, for later conditional injection.
   * @returns {Map<string, HTMLElement[]>} parentSlideId → cloned sub-slide elements, keyed by parent slide id.
   */
  #cacheSubSlides() {
    const map = new Map();
    this.querySelectorAll(`[${ATTRIBUTES.SWIPER.SLIDE}][${ATTRIBUTES.PARENT_ID}]`).forEach(
      (slide) => {
        const { parentId } = slide.dataset;
        const copy = slide.cloneNode(true);
        slide.remove();
        const existing = map.get(parentId) || [];
        existing.push(copy);
        map.set(parentId, existing);
      }
    );
    return map;
  }

  /**
   * Inserts cached sub-slides whose `data-parent-option` matches the selected input value
   * immediately after the parent slide in the Swiper instance.
   * @param {HTMLElement} parentSlide
   * @param {string} parentSlideId
   * @param {string} value - The selected option value used to filter matching sub-slides.
   */
  #addSubSlides(parentSlide, parentSlideId, value) {
    const subslidesToAdd =
      this.#subSlides
        .get(parentSlideId)
        ?.filter((subslide) => subslide.getAttribute(ATTRIBUTES.PARENT_OPTION) === value) || [];
    const index = this.#swiper?.slides?.indexOf(parentSlide) + 1 || 0;
    this.#swiper?.addSlide(index, subslidesToAdd);
  }

  /**
   * Removes all active sub-slides belonging to the given parent slide from the Swiper instance.
   * @param {string} parentSlideId
   */
  #removeSubSlides(parentSlideId) {
    const subslidesToRemove =
      this.#swiper?.slides?.filter(
        (subslide) => subslide.getAttribute(ATTRIBUTES.PARENT_ID) === parentSlideId
      ) || [];
    subslidesToRemove.forEach((subslide) => {
      this.select(subslide, ATTRIBUTES.PRODUCT_CARD.QUANTITY_INPUT, true).forEach((qi) => {
        const variantId = parseInt(qi.dataset.variantId);
        if (this.bundle.has(variantId)) this.removeItem(variantId);
      });
      const index = this.#swiper?.slides?.indexOf(subslide);
      this.#swiper?.removeSlide(index);
    });
  }

  /**
   * Adds or removes sub-slides for the parent slide of the given input, based on whether
   * the input is checked and has `data-has-suboptions`.
   * @param {HTMLInputElement} input
   */
  #updateSubSlides(input) {
    const parentSlide = input.closest(`[${ATTRIBUTES.SWIPER.SLIDE}]`);
    const parentSlideId = parentSlide.id;
    if (!this.#subSlides.has(parentSlide.id)) return;

    this.#removeSubSlides(parentSlideId);

    if (input.getAttribute(ATTRIBUTES.HAS_SUBOPTIONS) && input.checked) {
      this.#addSubSlides(parentSlide, parentSlideId, input.value);
    }
  }

  /**
   * Injects sub-slides for any slide that already has a checked option with sub-options
   * (e.g. default `checked` in markup or inputs set before this runs). Those cases do not
   * fire `input`, so they must be synced explicitly.
   */
  #syncSubSlidesFromCheckedInputs() {
    for (const parentSlideId of this.#subSlides.keys()) {
      const parentSlide = this.#swiper?.slides?.find((slide) => slide.id === parentSlideId);
      const input = parentSlide?.querySelector(
        `[${ATTRIBUTES.PRODUCT_CARD.SELECTION_INPUT}][${ATTRIBUTES.HAS_SUBOPTIONS}]:checked`
      );
      if (input) this.#updateSubSlides(input);
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * Handles a selection input change: updates sub-slides, syncs bundle selections, and refreshes UI.
   * @param {HTMLInputElement} input
   */
  #onInputChange(input) {
    this.#updateSubSlides(input);
    this.#updateSelections();

    this.updateUI();
  }

  /**
   * Navigates the Swiper to the slide matching the clicked step button's `data-target`.
   * @param {MouseEvent} event
   */
  #onStepButtonClick(event) {
    const targetSlideIndex = this.#swiper?.slides?.findIndex(
      (slide) => slide.id === event.currentTarget?.dataset?.target
    );
    this.#swiper?.slideTo(targetSlideIndex);
  }

  /** Triggers a full UI update whenever the active Swiper slide changes. */
  #onSlideChange() {
    this.updateUI();
  }

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  /**
   * Updates `[data-min-count]` elements within a slide to show how many more selections are needed.
   * @param {HTMLElement} slide
   */
  #updateRemainingCount(slide) {
    const remainingElements = [...this.select(slide, ATTRIBUTES.SUMMARY.MIN_COUNT, true)];
    const min = Number(slide.dataset.min) || 0;

    if (!remainingElements.length || min === 0) return;

    const totalCount = this.#getSlideCount(slide);
    let remaining = min - totalCount;
    if (remaining < 0) remaining = 0;

    remainingElements.forEach((element) => {
      element.textContent = remaining;
    });
  }

  /**
   * Diffs the previous and current selection sets and syncs the bundle Map accordingly:
   * adds newly selected variants, removes deselected ones, and caches product data as needed.
   */
  #updateSelections() {
    const newSelections = this.#getSelections();

    [...newSelections, ...this.#selections]
      ?.filter((selection) => this.#isVariant(selection))
      ?.forEach((selection) => {
        if (!this.productCache[selection]) {
          this.cacheCardData(selection);
        }

        if (!newSelections.includes(selection)) {
          this.removeItem(selection);
        } else if (!this.bundle.has(selection)) {
          this.addItem(selection, 1);
        }
      });

    this.#selections = newSelections;
  }

  // ---------------------------------------------------------------------------
  // Overrides
  // ---------------------------------------------------------------------------

  /**
   * Starter pack: Liquid already puts the bundle-discounted selling price in `data-current-price`
   * and the pre-discount reference in `data-compare-price` when available. We sum selling prices
   * for the payable total (no second application of `data-discount`) and sum {@link #listUnitPrice}
   * for the strikethrough list. Add-ons use full variant pricing from the same attributes.
   */
  updateTotal() {
    const { SUMMARY } = ATTRIBUTES;
    const listPriceEls = this.select(this, SUMMARY.LIST_PRICE, true);
    const totalPriceEls = this.select(this, SUMMARY.TOTAL_PRICE, true);

    if (!listPriceEls.length && !totalPriceEls.length) return;

    const discountPct = Number(this.dataset.discount) || 0;
    let sellingTotal = 0;
    let listTotal = 0;

    for (const [variantId, qty] of this.bundle) {
      const cached = this.productCache[variantId];
      if (!cached) continue;
      const sellingUnit = Number(cached.price);
      if (!Number.isFinite(sellingUnit)) continue;
      sellingTotal += qty * sellingUnit;
      listTotal += qty * this.#listUnitPrice(cached, discountPct, Boolean(cached.isAddon));
    }

    const showList = listTotal > sellingTotal + 0.0001;
    listPriceEls.forEach((el) => {
      el.textContent = showList ? this.formatMoney(listTotal) : '';
    });

    totalPriceEls.forEach((el) => {
      el.textContent = this.formatMoney(sellingTotal);
    });
  }

  /**
   * Unit “list” price in theme money units (pre–bundle-discount for core lines when inferrable).
   */
  #listUnitPrice(cached, discountPct, isAddon) {
    const p = Number(cached.price);
    if (!Number.isFinite(p)) return 0;
    const c = Number(cached.comparePrice);
    if (Number.isFinite(c) && c > p) return c;
    if (!isAddon && discountPct > 0) {
      const factor = 1 - discountPct / 100;
      if (factor > 0) return p / factor;
    }
    return p;
  }

  /**
   * Extends the base UI update with per-slide max enforcement, remaining-count display,
   * count display, and navigation state refresh.
   */
  updateUI() {
    super.updateUI();
    this.#swiper?.slides?.forEach((slide) => {
      this.updateQuantityInputMaxes(Number(slide.dataset.max), slide);
      this.#updateRemainingCount(slide);
      this.updateCountDisplay(slide);
    });
    this.#updateNavigationState();
  }

  /** Extends base serialisation to also persist the current radio/checkbox selections. */
  saveState() {
    const state = {
      bundle: [...this.bundle.entries()],
      productCache: this.productCache,
      selections: this.#selections,
    };
    localStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  /**
   * Restores base bundle state, rebuilds summary list items, and re-checks persisted
   * radio/checkbox selection inputs.
   */
  restoreState() {
    super.restoreState();

    this.bundle.keys().forEach((variantId) => this.buildListItem(variantId));

    const selections = JSON.parse(localStorage.getItem(this.storageKey))?.selections || [];
    if (selections.length > 0) {
      this.#selections = selections;
      this.#restorePersistedSelectionInputs();
      super.updateInputValues();
    }
  }

  /**
   * Re-checks persisted radio/checkbox inputs and rebuilds sub-slides once after restore.
   * Must not run from {@link updateUI}: calling {@link #updateSubSlides} there would remove
   * and re-clone sub-slides on every bundle sync, clearing quantities from the bundle.
   */
  #restorePersistedSelectionInputs() {
    this.#selections.forEach((selection) => {
      const input = this.querySelector(
        `[${ATTRIBUTES.PRODUCT_CARD.SELECTION_INPUT}][value="${selection}"]`
      );
      if (!input) return;
      if (!input.checked) input.checked = true;
      if (input.getAttribute(ATTRIBUTES.HAS_SUBOPTIONS)) this.#updateSubSlides(input);
    });
  }

  clearBundle(deep = false) {
    this.#selections = [];
    super.clearBundle(deep);
  }

  /**
   * Updates `[data-current-quantity]` elements scoped to the given slide with its item count.
   * @param {HTMLElement} [slide]
   */
  updateCountDisplay(slide) {
    this.select(slide, ATTRIBUTES.SUMMARY.CURRENT_QUANTITY, true)?.forEach((el) => {
      el.textContent = this.#getSlideCount(slide);
    });
  }

  /**
   * Returns true only when the active slide is the overview slide and every slide satisfies
   * its minimum selection requirement.
   * @returns {boolean}
   */
  isComplete() {
    const currentSlide = this.#getCurrentSlide();
    const isOverviewSlide = currentSlide.dataset.type === 'overview';

    return isOverviewSlide && this.#isSlideComplete(currentSlide);
  }

  /**
   * Redirects to the cart page after adding the bundle to the cart.
   */
  async handleAddToCart() {
    this.#loader.show();
    await super.handleAddToCart().then(() => {
      window.location.href = window.routes?.cart_url || '/cart';
    });
  }

  /**
   * Maps the cart items to account for add-ons and submits the mapped items to the cart.
   * @param {Array<{ id: number, quantity: number, properties: Record<string, string> }>} items
   * @returns {Promise<void>}
   */
  async submitToCart(items) {
    const mappedItems = this.#mapCartItems(items);
    await super.submitToCart(mappedItems);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns true if the slide has no minimum requirement, or if its total item count meets it.
   * @param {HTMLElement} slide
   * @returns {boolean}
   */
  #isSlideComplete(slide) {
    if (!slide) return false;

    if (slide.dataset.type === 'overview')
      return this.#swiper?.slides
        ?.filter((slide) => slide.dataset.type !== 'overview')
        .every((slide) => this.#isSlideComplete(slide));

    const minSelection = Number(slide.dataset.min) || 0;
    if (minSelection === 0) return true;

    const totalCount = this.#getSlideCount(slide);
    return totalCount >= minSelection;
  }

  /**
   * Returns the total number of selected items in a slide, combining checked inputs and quantity inputs.
   * @param {HTMLElement} slide
   * @returns {number}
   */
  #getSlideCount(slide) {
    const checkedCount = slide.querySelectorAll(
      `[${ATTRIBUTES.PRODUCT_CARD.SELECTION_INPUT}]:checked`
    ).length;

    const quantityOptions = [...this.select(slide, ATTRIBUTES.PRODUCT_CARD.QUANTITY_INPUT, true)];
    const quantityCount = quantityOptions?.reduce(
      (acc, option) => acc + Number(option.querySelector('input')?.value || 0),
      0
    );

    return checkedCount + quantityCount || 0;
  }

  /**
   * Returns the currently active Swiper slide element.
   * @returns {HTMLElement | undefined}
   */
  #getCurrentSlide() {
    return this.#swiper?.slides?.[this.#swiper.activeIndex];
  }

  /**
   * Reads all form data and returns the selected input values, excluding quantity fields.
   * @returns {Array<string>}
   */
  #getSelections() {
    const formData = new FormData(this.#form);
    return [...formData.entries()].filter(([key]) => key !== 'quantity').map(([, value]) => value);
  }

  /**
   * Returns true if the given value is a positive numeric variant ID.
   * Used to distinguish variant selections from non-numeric option values.
   * @param {string} value
   * @returns {boolean}
   */
  #isVariant(value) {
    return !Number.isNaN(Number(value)) && Number(value) > 0;
  }

  /**
   * Formats the cart items to account for add-ons.
   * @param {Array<{ id: number, quantity: number, properties: Record<string, string> }>} items
   * @returns {Array<{ id: number, quantity: number, properties: Record<string, string> }>}
   */
  #mapCartItems(items) {
    const nonAddOnItemIds = items
      .filter((item) => !this.productCache[item.id].isAddon)
      .map((item) => item.id);

    return items.map((item) => {
      // If the item is an add-on, remove the _bundle_component_quantity property.
      if (!nonAddOnItemIds.includes(item.id)) {
        delete item.properties._bundle_component_quantity;
      }

      // Create new _bundle_components property in the parent item with the add-on items excluded.
      if (item.properties._bundle_components) {
        const parsedComponents = JSON.parse(item.properties._bundle_components);
        item.properties._bundle_components = JSON.stringify(
          parsedComponents.filter((component) => nonAddOnItemIds.includes(component.id))
        );
      }

      return item;
    });
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
